import axios from 'axios';
import React, { useMemo, useState, useEffect } from 'react';

import { useScreenClass } from 'react-grid-system';
import styled from 'styled-components';
import theme from '../styles/theme';
import Status from '../types/enum/Status';
import Button from './Button';
import FlexBox, { FlexGrow } from './FlexBox';
import Icon, { ClickableIcon } from './Icon';
import Input from './Input';
import Modal from './Modal';
import { Col, Row } from './StyledGridSystem';
import { RightAlign } from './UtilsComponents';

type SectionDeleteModalProps = {
  isOpen?: boolean;
  setOpen: (x: boolean) => void;
  currentSection: { [key: string]: any };
  openParent: any;
  showSections?: boolean;
  // handleDeleteSection: (regId: string) => void
  onDeleteComplete?: () => void;
  onDeleteError?: (err: any) => void;
};

const wait = async (ms: number) => new Promise((resolve, reject) => {
  setTimeout(resolve, ms);
});

const SectionDeleteModal = ({
  isOpen,
  setOpen,
  openParent,
  currentSection,
  // handleDeleteSection,
  onDeleteError,
  onDeleteComplete,
}: SectionDeleteModalProps): JSX.Element => {
  const screenClass = useScreenClass();
  const [deleteConfirmation, setDeleteconfirmation] = useState<boolean>(true);
  const [deletePasswordInput, setDeletePasswordInput] = useState<string>('');
  const [isOpenComplete, setOpenComplete] = useState<boolean>(false);
  const [isOpenError, setOpenError] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);

  const handleDeleteSection = async (regId: string) => {
    setLoading(true);
    axios
      .delete(`/api/v1/courses/sections/${regId}`)
      .then((res) => {
        setLoading(false);
        setOpenComplete(true);
      })
      .catch((err) => {
        setLoading(false);
        setOpenError(true);
        setError(err);
      });
  };

  const onComplete = () => {
    setOpenComplete(false);
    onDeleteComplete?.();
  };

  const onError = () => {
    setOpenError(false);
    onDeleteError?.(error);
  };

  useEffect(() => {
    if (isOpen) {
      setDeletePasswordInput('');
    }
  }, [isOpen]);

  const deletePassword = useMemo(() => {
    const verb = 'delete';
    return `${verb} ${currentSection.name}`;
  }, [currentSection]);

  return (
    <>
      <Modal
        isOpen={isOpen || false}
        setOpen={setOpen}
        customStyle={{ overlay: { zIndex: 500 } }}>
        <FlexBox>
          <FlexGrow>
            <h3>Deleting Section</h3>
          </FlexGrow>
          <ClickableIcon name="x-s" onClick={() => setOpen(false)} />
        </FlexBox>
        {/* <p style={{ textAlign: 'center' }}>
          <img src="/alert-warning.svg" alt="alert warning"/>
        </p> */}
        <p>
          Are you sure you want to delete the section "
          <b>{currentSection.name}</b>" of the course "
          <b>{currentSection.short_name}</b>"? This cannot be undone.
        </p>
        <p>
          To confirm, type "<b>{deletePassword}</b>" in this box.
        </p>
        <Input
          isFullWidth={true}
          value={deletePasswordInput}
          onInput={(e: any) => {
            setDeletePasswordInput(e.target.value);
          }}
        />
        <RightAlign style={{ marginTop: theme.spacing.x2 }}>
          <Button
            isOutline
            onClick={() => {
              setOpen(false);
              openParent();
            }}
            status={Status.default}>
            Cancel
          </Button>
          <Button
            disabled={isLoading || deletePasswordInput !== deletePassword}
            status={Status.primary}
            onClick={() => handleDeleteSection(currentSection.id)}>
            {isLoading && (
              <>
                <Icon mr={1} name="spinner" spin />
                Deleting...
              </>
            )}
            {!isLoading && (
              <>
                <Icon mr={1} name="trash" />
                Delete section
              </>
            )}
          </Button>
        </RightAlign>
      </Modal>

      <Modal
        isOpen={isOpenComplete}
        setOpen={setOpenComplete}
        onClose={onComplete}
        customStyle={{ overlay: { zIndex: 600 }, content: { textAlign: 'center' } }}>
        <img
          src="/alert-success-soft.svg"
          alt="alert warning"
          style={{ margin: 'auto' }}
        />
        <h3>Section Deleting Complete</h3>
        <p>
          The section "
          <b>
            {currentSection.code} {currentSection.short_name}
          </b>
          " has been removed.
        </p>
        <Button status={Status.primary} onClick={onComplete}>
          Done
        </Button>
      </Modal>

      <Modal
        isOpen={isOpenError}
        setOpen={setOpenError}
        onClose={onError}
        customStyle={{ overlay: { zIndex: 600 }, content: { textAlign: 'center' } }}>
        <img
          src="/alert-warning.svg"
          alt="alert warning"
          style={{ margin: 'auto' }}
        />
        <h3>Section Delete Error</h3>
        <p>
          An error occured while deleting the section "
          <b>
            {currentSection.code} {currentSection.short_name}
          </b>
          ". Please try again or contact us if the problem persists.
        </p>
        <Button status={Status.primary} onClick={onError}>
          Done
        </Button>
      </Modal>
    </>
  );
};

export default SectionDeleteModal;
